import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { toaster } from '../utils/toast';
import { getRequest } from '../utils/axiosClient';
import useUploadQueue from './useUploadQueue';
import { prepFileDataForDedicatedUpload, filterDuplicateFilesByName } from '../utils/helper';
import { resolveRecordFiles } from '../Utils';
import { RECORD_FILE_CONFIG } from '../utils/enum';
import Constants from '../Constants';

const useRecordFiles = (caseId, recordType) => {
  const { defaultName, uploadSuccessMessage } = RECORD_FILE_CONFIG[recordType];

  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const { isLoading: isUploading, queueFiles } = useUploadQueue(caseId);

  const uploadQueue = useSelector((state) => state.fileUpload?.uploadQueue ?? []);
  const activeUploads = useSelector((state) => state.fileUpload?.activeUploads ?? {});
  const isUploadPending = uploadQueue.length > 0 || Object.keys(activeUploads).length > 0;

  const waitingForUploadRef = useRef(false);

  const fetchFiles = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getRequest({ url: `${Constants.ApiUrl.records.get}/${caseId}/${recordType}` });
      setFiles(
        response?.data?.data.map((item, index) => ({
          ...item,
          name: item.originalFileName || `${defaultName} ${index + 1}`
        }))
      );
    } catch (error) {
      console.error(`Error fetching ${defaultName}:`, error);
    } finally {
      setIsLoading(false);
    }
  }, [caseId, recordType]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  useEffect(() => {
    if (waitingForUploadRef.current && !isUploadPending) {
      waitingForUploadRef.current = false;
      fetchFiles();
      toaster({ message: uploadSuccessMessage, success: true });
    }
  }, [isUploadPending]);

  const uploadFiles = useCallback(async ({ file: newFiles } = {}) => {
    if (!newFiles?.length) return;

    const uniqueFiles = filterDuplicateFilesByName({ newFiles, prevFiles: files });
    if (!uniqueFiles.length) return;

    const prepFiles = prepFileDataForDedicatedUpload(uniqueFiles);
    waitingForUploadRef.current = true;

    const uploadedFile = await queueFiles({ files: prepFiles, caseRecordType: recordType });
    const resolved = resolveRecordFiles(uploadedFile?.filesWithMetadata, defaultName);
    setFiles(prev => [...prev, ...resolved]);
  }, [queueFiles, files, recordType]);

  return { files, isLoading, isUploading, uploadFiles, refetch: fetchFiles, isUploadPending };
};

export default useRecordFiles;
